/* ─────────────────────────────────────────────────────────────────────────
 * Pixel: everything the cabinet paints on a canvas, in PAL colours only.
 *
 *  - A 5×7 alphabet. The title letters are that alphabet doubled and grown
 *    a cell in every direction, then banded, hatched, bevelled, extruded
 *    and outlined cell by cell into one small canvas per letter.
 *  - Caption sprites (year, role) in the plain 5×7 alphabet.
 *  - ScreenScene: the low-resolution screen. A dithered sky, clouds that
 *    drift a whole pixel at a time, and a floating island.
 *  - The portrait: a drawn pixel bust, or a photo repainted in the palette.
 *
 * Nothing here touches the DOM on import. Canvases are only created when a
 * function is called, which the cabinet does from its effects.
 * ───────────────────────────────────────────────────────────────────────── */

import { PAL } from '@/components/landing/cabinet/data'

type Color = string
type RGB = [number, number, number]
type Puff = { x: number; y: number; r: number }

/* ── Helpers ──────────────────────────────────────────────────────────── */

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v))

const makeCanvas = (w: number, h: number): HTMLCanvasElement => {
    const c = document.createElement('canvas')
    c.width = Math.max(1, Math.round(w))
    c.height = Math.max(1, Math.round(h))
    return c
}

const hexRgb = (hex: string): RGB => [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16)
]

/* 4×4 ordered dither: a cell takes the second colour when its mix beats the threshold */
const BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5]
const bayer = (x: number, y: number): number => (BAYER[(y & 3) * 4 + (x & 3)] + 0.5) / 16

/* seeded random, so every resize repaints the same little world */
const seeded = (seed: number) => (): number => {
    seed = (seed + 0x6d2b79f5) | 0
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
}

/* A small grid of palette cells, painted to a canvas at any whole-pixel scale */
class Grid {
    readonly w: number
    readonly h: number
    private cells: (Color | null)[]

    constructor(w: number, h: number) {
        this.w = w
        this.h = h
        this.cells = new Array<Color | null>(w * h).fill(null)
    }

    has(x: number, y: number): boolean {
        return x >= 0 && y >= 0 && x < this.w && y < this.h && this.cells[y * this.w + x] !== null
    }

    set(x: number, y: number, c: Color): void {
        if (x >= 0 && y >= 0 && x < this.w && y < this.h) this.cells[y * this.w + x] = c
    }

    /* ring every filled shape in `color`; `diagonal` also takes the corner neighbours */
    outline(color: Color, diagonal = false): void {
        const ring: number[] = []
        for (let y = 0; y < this.h; y++) {
            for (let x = 0; x < this.w; x++) {
                if (this.has(x, y)) continue
                const touches =
                    this.has(x - 1, y) || this.has(x + 1, y) || this.has(x, y - 1) || this.has(x, y + 1) ||
                    (diagonal && (this.has(x - 1, y - 1) || this.has(x + 1, y - 1) || this.has(x - 1, y + 1) || this.has(x + 1, y + 1)))
                if (touches) ring.push(y * this.w + x)
            }
        }
        ring.forEach((i) => {
            this.cells[i] = color
        })
    }

    paint(ctx: CanvasRenderingContext2D, scale = 1): void {
        for (let y = 0; y < this.h; y++) {
            for (let x = 0; x < this.w; x++) {
                const c = this.cells[y * this.w + x]
                if (c === null) continue
                ctx.fillStyle = c
                ctx.fillRect(x * scale, y * scale, scale, scale)
            }
        }
    }

    toCanvas(scale = 1): HTMLCanvasElement {
        const c = makeCanvas(this.w * scale, this.h * scale)
        this.paint(c.getContext('2d')!, scale)
        return c
    }
}

/* soft round shapes (clouds, canopies), lit from the upper left and dithered into shade */
const paintPuffs = (grid: Grid, puffs: Puff[], base: Color, lit: Color): void => {
    const x0 = Math.floor(Math.min(...puffs.map((p) => p.x - p.r)))
    const x1 = Math.ceil(Math.max(...puffs.map((p) => p.x + p.r)))
    const y0 = Math.floor(Math.min(...puffs.map((p) => p.y - p.r)))
    const y1 = Math.ceil(Math.max(...puffs.map((p) => p.y + p.r)))
    const cx = (x0 + x1) / 2
    const cy = (y0 + y1) / 2
    const span = Math.max(1, (x1 - x0 + y1 - y0) / 2)
    for (let y = y0; y <= y1; y++) {
        for (let x = x0; x <= x1; x++) {
            const px = x + 0.5
            const py = y + 0.5
            if (!puffs.some((p) => (px - p.x) ** 2 + (py - p.y) ** 2 <= p.r * p.r)) continue
            const light = (cx - px + cy - py) / span
            grid.set(x, y, light > 0.15 + bayer(x, y) * 0.7 ? lit : base)
        }
    }
}

/* ── The 5×7 alphabet ─────────────────────────────────────────────────── */

const FONT: Record<string, string[]> = {
    'A': ['.###.', '#...#', '#...#', '#####', '#...#', '#...#', '#...#'],
    'B': ['####.', '#...#', '#...#', '####.', '#...#', '#...#', '####.'],
    'C': ['.###.', '#...#', '#....', '#....', '#....', '#...#', '.###.'],
    'D': ['####.', '#...#', '#...#', '#...#', '#...#', '#...#', '####.'],
    'E': ['#####', '#....', '#....', '####.', '#....', '#....', '#####'],
    'F': ['#####', '#....', '#....', '####.', '#....', '#....', '#....'],
    'G': ['.###.', '#...#', '#....', '#.###', '#...#', '#...#', '.####'],
    'H': ['#...#', '#...#', '#...#', '#####', '#...#', '#...#', '#...#'],
    'I': ['###', '.#.', '.#.', '.#.', '.#.', '.#.', '###'],
    'J': ['..###', '...#.', '...#.', '...#.', '...#.', '#..#.', '.##..'],
    'K': ['#...#', '#..#.', '#.#..', '##...', '#.#..', '#..#.', '#...#'],
    'L': ['#....', '#....', '#....', '#....', '#....', '#....', '#####'],
    'M': ['#...#', '##.##', '#.#.#', '#.#.#', '#...#', '#...#', '#...#'],
    'N': ['#...#', '#...#', '##..#', '#.#.#', '#..##', '#...#', '#...#'],
    'O': ['.###.', '#...#', '#...#', '#...#', '#...#', '#...#', '.###.'],
    'P': ['####.', '#...#', '#...#', '####.', '#....', '#....', '#....'],
    'Q': ['.###.', '#...#', '#...#', '#...#', '#.#.#', '#..#.', '.##.#'],
    'R': ['####.', '#...#', '#...#', '####.', '#.#..', '#..#.', '#...#'],
    'S': ['.####', '#....', '#....', '.###.', '....#', '....#', '####.'],
    'T': ['#####', '..#..', '..#..', '..#..', '..#..', '..#..', '..#..'],
    'U': ['#...#', '#...#', '#...#', '#...#', '#...#', '#...#', '.###.'],
    'V': ['#...#', '#...#', '#...#', '#...#', '#...#', '.#.#.', '..#..'],
    'W': ['#...#', '#...#', '#...#', '#.#.#', '#.#.#', '#.#.#', '.#.#.'],
    'X': ['#...#', '#...#', '.#.#.', '..#..', '.#.#.', '#...#', '#...#'],
    'Y': ['#...#', '#...#', '.#.#.', '..#..', '..#..', '..#..', '..#..'],
    'Z': ['#####', '....#', '...#.', '..#..', '.#...', '#....', '#####'],
    '0': ['.###.', '#...#', '#..##', '#.#.#', '##..#', '#...#', '.###.'],
    '1': ['.#.', '##.', '.#.', '.#.', '.#.', '.#.', '###'],
    '2': ['.###.', '#...#', '....#', '...#.', '..#..', '.#...', '#####'],
    '3': ['####.', '....#', '....#', '.###.', '....#', '....#', '####.'],
    '4': ['...#.', '..##.', '.#.#.', '#..#.', '#####', '...#.', '...#.'],
    '5': ['#####', '#....', '####.', '....#', '....#', '#...#', '.###.'],
    '6': ['.###.', '#....', '#....', '####.', '#...#', '#...#', '.###.'],
    '7': ['#####', '....#', '...#.', '..#..', '.#...', '.#...', '.#...'],
    '8': ['.###.', '#...#', '#...#', '.###.', '#...#', '#...#', '.###.'],
    '9': ['.###.', '#...#', '#...#', '.####', '....#', '....#', '.###.'],
    '.': ['.', '.', '.', '.', '.', '.', '#'],
    ',': ['..', '..', '..', '..', '..', '.#', '#.'],
    '-': ['...', '...', '...', '###', '...', '...', '...'],
    '—': ['.....', '.....', '.....', '#####', '.....', '.....', '.....'],
    '&': ['.##..', '#..#.', '#.#..', '.#...', '#.#.#', '#..#.', '.##.#'],
    '/': ['....#', '....#', '...#.', '..#..', '.#...', '#....', '#....'],
    ':': ['.', '.', '#', '.', '.', '#', '.'],
    '!': ['#', '#', '#', '#', '#', '.', '#'],
    '?': ['.###.', '#...#', '....#', '...#.', '..#..', '.....', '..#..'],
    "'": ['#', '#', '.', '.', '.', '.', '.'],
    '+': ['.....', '..#..', '..#..', '#####', '..#..', '..#..', '.....'],
    '·': ['.', '.', '.', '#', '.', '.', '.'],
    '(': ['.#', '#.', '#.', '#.', '#.', '#.', '.#'],
    ')': ['#.', '.#', '.#', '.#', '.#', '.#', '#.']
}

const SPACE = 3

type Glyph = { w: number; on: (x: number, y: number) => boolean }

const glyphs = new Map<string, Glyph | null>()

/* accents are dropped (É draws as E); anything missing draws as a space */
const glyph = (raw: string): Glyph | null => {
    const ch = raw.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase()
    const cached = glyphs.get(ch)
    if (cached !== undefined) return cached
    const rows = FONT[ch]
    const g: Glyph | null = rows
        ? { w: rows[0].length, on: (x, y) => x >= 0 && y >= 0 && x < rows[0].length && y < 7 && rows[y][x] === '#' }
        : null
    glyphs.set(ch, g)
    return g
}

/* ── Title letters ────────────────────────────────────────────────────── */

const FACE_H = 16 // seven rows, doubled, grown a cell top and bottom
const DEPTH = 3 // cells of hatched extrusion under the face

type Mask = { w: number; h: number; on: (x: number, y: number) => boolean }

/* The glyph doubled and grown a cell in every direction. Growth never
 * enters a gap between two strokes, so counters stay open and the strokes
 * of M, W, K and X stay apart. */
const boldMask = (g: Glyph): Mask => {
    const w = g.w * 2 + 2
    const h = FACE_H
    const src = (x: number, y: number): boolean => x >= 1 && y >= 1 && g.on((x - 1) >> 1, (y - 1) >> 1)
    const cells: boolean[] = []
    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            let on = src(x, y)
            if (!on) {
                const near =
                    src(x - 1, y - 1) || src(x, y - 1) || src(x + 1, y - 1) || src(x - 1, y) ||
                    src(x + 1, y) || src(x - 1, y + 1) || src(x, y + 1) || src(x + 1, y + 1)
                const squeezedX = (src(x - 1, y) || src(x - 2, y)) && (src(x + 1, y) || src(x + 2, y))
                const squeezedY = (src(x, y - 1) || src(x, y - 2)) && (src(x, y + 1) || src(x, y + 2))
                on = near && !squeezedX && !squeezedY
            }
            cells.push(on)
        }
    }
    return { w, h, on: (x, y) => x >= 0 && y >= 0 && x < w && y < h && cells[y * w + x] }
}

const BANDS: Color[] = [PAL.creamHi, PAL.tan, PAL.peach, PAL.rust]
const LIGHTER: Record<Color, Color> = {
    [PAL.creamHi]: PAL.creamHi,
    [PAL.tan]: PAL.creamHi,
    [PAL.peach]: PAL.tan,
    [PAL.rust]: PAL.peach
}
const DARKER: Record<Color, Color> = {
    [PAL.creamHi]: PAL.tan,
    [PAL.tan]: PAL.peach,
    [PAL.peach]: PAL.rust,
    [PAL.rust]: PAL.plum
}

/* four warm bands, each one's first row hatched into the band above,
 * bevelled with light from the top left */
const faceColor = (m: Mask, x: number, y: number): Color => {
    let band = Math.min(3, y >> 2)
    if (band > 0 && (y & 3) === 0 && ((x + y) & 1) === 0) band -= 1
    const c = BANDS[band]
    if (!m.on(x, y - 1)) return PAL.creamHi
    if (!m.on(x, y + 1) || !m.on(x + 1, y)) return DARKER[c]
    if (!m.on(x - 1, y)) return LIGHTER[c]
    return c
}

/* the extrusion: teal, hatched with plum diagonals */
const sideColor = (x: number, y: number): Color => ((x + y) % 3 === 0 ? PAL.plum : PAL.teal)

export type LetterSprite = {
    canvas: HTMLCanvasElement
    gw: number // face width, device px
    gh: number // face plus extrusion height, device px
    ox: number // where that box starts inside the canvas (the outline)
    oy: number
}

/* how far the pen moves after a letter, in cells: outlines just touch */
export const boldAdvanceCells = (ch: string): number => {
    const g = glyph(ch)
    return g ? g.w * 2 + 4 : 6
}

export const boldHeightCells = (_ch: string): number => FACE_H + DEPTH

export const letterSprite = (ch: string, u: number): LetterSprite => {
    const g = glyph(ch)
    if (!g) return { canvas: makeCanvas(1, 1), gw: 0, gh: 0, ox: 0, oy: 0 }
    const m = boldMask(g)
    const under = (x: number, y: number): boolean => {
        for (let d = 1; d <= DEPTH; d++) {
            if (m.on(x, y - d)) return true
        }
        return false
    }
    const grid = new Grid(m.w + 2, m.h + DEPTH + 2)
    for (let y = 0; y < m.h + DEPTH; y++) {
        for (let x = 0; x < m.w; x++) {
            if (m.on(x, y)) grid.set(x + 1, y + 1, faceColor(m, x, y))
            else if (under(x, y)) grid.set(x + 1, y + 1, sideColor(x, y))
        }
    }
    grid.outline(PAL.plum, true)
    return { canvas: grid.toCanvas(u), gw: m.w * u, gh: (m.h + DEPTH) * u, ox: u, oy: u }
}

/* ── Captions ─────────────────────────────────────────────────────────── */

/* plain 5×7 text in plum with a one-cell tan drop shadow */
export const captionSprite = (text: string, u: number): HTMLCanvasElement => {
    const gs = [...text].map((c) => glyph(c))
    const width = gs.reduce((s, g, i) => s + (g ? g.w : SPACE) + (i < gs.length - 1 ? 1 : 0), 0)
    const grid = new Grid(width + 1, 8)
    const ink: [number, number][] = []
    let pen = 0
    gs.forEach((g) => {
        if (g) {
            for (let y = 0; y < 7; y++) {
                for (let x = 0; x < g.w; x++) {
                    if (g.on(x, y)) ink.push([pen + x, y])
                }
            }
        }
        pen += (g ? g.w : SPACE) + 1
    })
    ink.forEach(([x, y]) => grid.set(x + 1, y + 1, PAL.tan))
    ink.forEach(([x, y]) => grid.set(x, y, PAL.plum))
    return grid.toCanvas(u)
}

/* ── The screen ───────────────────────────────────────────────────────── */

/* [from, to, colour A, colour B, share of B at from, share of B at to] */
const SKY: [number, number, Color, Color, number, number][] = [
    [0, 0.1, PAL.sage, PAL.cream, 0, 0.06],
    [0.1, 0.48, PAL.sage, PAL.cream, 0.06, 1],
    [0.48, 0.86, PAL.cream, PAL.tan, 0, 0],
    [0.86, 1, PAL.cream, PAL.tan, 0, 0.3]
]

const paintSky = (w: number, h: number): HTMLCanvasElement => {
    const c = makeCanvas(w, h)
    const ctx = c.getContext('2d')!
    const img = ctx.createImageData(c.width, c.height)
    for (let y = 0; y < c.height; y++) {
        const fy = c.height > 1 ? y / (c.height - 1) : 0
        const seg = SKY.find((s) => fy <= s[1]) ?? SKY[SKY.length - 1]
        const k = clamp((fy - seg[0]) / (seg[1] - seg[0]), 0, 1)
        const mix = seg[4] + (seg[5] - seg[4]) * k
        const a = hexRgb(seg[2])
        const b = hexRgb(seg[3])
        for (let x = 0; x < c.width; x++) {
            const p = mix > bayer(x, y) ? b : a
            const i = (y * c.width + x) * 4
            img.data[i] = p[0]
            img.data[i + 1] = p[1]
            img.data[i + 2] = p[2]
            img.data[i + 3] = 255
        }
    }
    ctx.putImageData(img, 0, 0)
    return c
}

/* a cumulus: a row of puffs, tallest in the middle, on a flat sunlit base */
const cloudSprite = (cw: number, ch: number, rand: () => number): HTMLCanvasElement => {
    const grid = new Grid(cw + 2, ch + 2)
    const base = ch
    const n = cw > 18 ? 4 : 3
    const puffs: Puff[] = []
    for (let i = 0; i < n; i++) {
        const t = i / (n - 1)
        const r = ch * (0.34 + 0.34 * (1 - Math.abs(t - 0.5) * 2) + rand() * 0.1)
        puffs.push({ x: 1 + r + (cw - 2 * r) * t, y: Math.max(1 + r, base + 1 - r * 0.6), r })
    }
    const slabL = puffs[0].x
    const slabR = puffs[n - 1].x
    for (let y = 1; y <= base; y++) {
        for (let x = 1; x <= cw; x++) {
            const px = x + 0.5
            const py = y + 0.5
            const inPuff = puffs.some((p) => (px - p.x) ** 2 + (py - p.y) ** 2 <= p.r * p.r)
            const inSlab = y >= base - 1 && px >= slabL && px <= slabR
            if (!inPuff && !inSlab) continue
            const fromBase = base - y
            const shade = fromBase === 0 || (fromBase === 1 && bayer(x, y) < 0.5)
            grid.set(x, y, shade ? PAL.tan : PAL.creamHi)
        }
    }
    grid.outline(PAL.tan)
    return grid.toCanvas()
}

const SOIL: Color[] = [PAL.tan, PAL.peach, PAL.rust, PAL.plum]

type Island = { canvas: HTMLCanvasElement; ground: number; left: number; right: number; soil: number }

/* grass on top, a ragged upside-down cone of dithered strata below, a tree
 * and a bush on the lawn. `ground` is the row of the grass line. */
const islandSprite = (iw: number, rand: () => number): Island => {
    const soilH = Math.max(5, Math.round(iw * 0.36))
    const canopyR = Math.max(2, Math.round(iw * 0.09))
    const trunkH = Math.max(2, Math.round(iw * 0.07))
    const ground = canopyR * 2 + trunkH + 3
    const grid = new Grid(iw + 6, ground + 2 + soilH + 2)
    const left = 3
    const right = left + iw - 1
    const mid0 = left + iw / 2
    const tilt = (rand() - 0.5) * iw * 0.2

    // soil, lit from the left, darker the deeper it hangs
    let jagL = 0
    let jagR = 0
    for (let r = 0; r < soilH; r++) {
        if (r % 2 === 1) {
            jagL = rand() < 0.5 ? 1 : 0
            jagR = rand() < 0.5 ? 1 : 0
        }
        const k = r / soilH
        const half = Math.max(0.5, (iw / 2) * (1 - Math.pow(k, 1.25)))
        const mid = mid0 + tilt * k
        const x0 = Math.round(mid - half) + jagL
        const x1 = Math.max(x0, Math.round(mid + half) - 1 - jagR)
        const y = ground + 2 + r
        for (let x = x0; x <= x1; x++) {
            const side = clamp((x + 0.5 - mid) / half, -1, 1)
            const depth = clamp(k * 3.4 + side * 0.6 + 0.1, 0, 3.99)
            const band = Math.floor(depth)
            grid.set(x, y, SOIL[depth - band > bayer(x, y) ? Math.min(3, band + 1) : band])
        }
    }

    // grass: a lit edge, an overhanging lip, a few blades and drips
    for (let x = left + 1; x <= right - 1; x++) grid.set(x, ground, PAL.sage)
    for (let x = left - 1; x <= right + 1; x++) grid.set(x, ground + 1, (x * 7) % 5 === 0 ? PAL.sage : PAL.teal)
    grid.set(left - 1, ground + 2, PAL.teal)
    grid.set(right + 1, ground + 2, PAL.teal)
    if (iw >= 24) grid.set(right + 1, ground + 3, PAL.teal)
    for (let i = 0; i < Math.max(2, Math.round(iw / 8)); i++) {
        grid.set(left + 2 + Math.floor(rand() * (iw - 4)), ground - 1, PAL.sage)
    }

    // a tree right of centre, a bush to its left
    const wide = iw >= 34
    const tx = Math.round(mid0 + iw * 0.16)
    for (let y = ground - trunkH; y < ground; y++) {
        grid.set(tx, y, PAL.rust)
        if (wide) grid.set(tx + 1, y, PAL.rust)
    }
    const cx = tx + (wide ? 1 : 0.5)
    const cy = ground - trunkH - canopyR + 0.5
    paintPuffs(grid, [
        { x: cx - canopyR * 0.6, y: cy + canopyR * 0.3, r: canopyR * 0.85 },
        { x: cx + canopyR * 0.6, y: cy + canopyR * 0.3, r: canopyR * 0.85 },
        { x: cx, y: cy - canopyR * 0.25, r: canopyR * 0.95 }
    ], PAL.teal, PAL.sage)
    paintPuffs(grid, [{ x: mid0 - iw * 0.24, y: ground - canopyR * 0.3, r: Math.max(1.5, canopyR * 0.62) }], PAL.teal, PAL.sage)

    grid.outline(PAL.plum)
    return { canvas: grid.toCanvas(), ground, left, right, soil: soilH }
}

/* a loose pebble: lit top, rust underside, rounded bottom corners */
const rockSprite = (w: number, h: number): HTMLCanvasElement => {
    const grid = new Grid(w + 2, h + 2)
    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            const corner = y === h - 1 && (x === 0 || x === w - 1) && w > 2
            if (!corner) grid.set(x + 1, y + 1, y === 0 ? PAL.tan : y === h - 1 ? PAL.rust : PAL.peach)
        }
    }
    grid.outline(PAL.plum)
    return grid.toCanvas()
}

type Drifter = { sprite: HTMLCanvasElement; x: number; y: number; speed: number }
type Floater = { sprite: HTMLCanvasElement; x: number; y: number; amp: number; freq: number; phase: number }

/* The screen at low resolution: the cabinet scales it up with smoothing
 * off, so every drift of a cloud is a whole-pixel step. Clouds sit in the
 * upper half; the island floats low and centred, under the prompt. */
export class ScreenScene {
    private readonly w: number
    private readonly h: number
    private readonly sky: HTMLCanvasElement
    private readonly clouds: Drifter[] = []
    private readonly floaters: Floater[] = []
    private t = 0

    constructor(w: number, h: number) {
        this.w = w
        this.h = h
        this.sky = paintSky(w, h)
        if (w < 24 || h < 24) return
        const rand = seeded(1979)
        const s = clamp(Math.min(w, h * 1.8) / 260, 0.55, 1.3)
        const area = w * h
        this.addClouds(clamp(Math.round(area / 9000), 2, 6), false, s, rand)
        this.addClouds(clamp(Math.round(area / 13000), 2, 4), true, s, rand)
        this.addIsland(rand)
    }

    private addClouds(count: number, near: boolean, s: number, rand: () => number): void {
        for (let i = 0; i < count; i++) {
            const cw = Math.max(9, Math.round((near ? 24 + rand() * 14 : 13 + rand() * 8) * s))
            const ch = Math.max(4, Math.round((near ? 8 + rand() * 3 : 5 + rand() * 2) * s))
            const sprite = cloudSprite(cw, ch, rand)
            const span = this.w + sprite.width
            this.clouds.push({
                sprite,
                x: ((i + 0.15 + rand() * 0.7) / count) * span - sprite.width,
                y: Math.round(this.h * (near ? 0.07 + rand() * 0.36 : 0.03 + rand() * 0.24)),
                speed: near ? 2.4 + rand() * 2 : 1 + rand() * 1.1
            })
        }
    }

    private addIsland(rand: () => number): void {
        const { w, h } = this
        const portrait = w < h
        const iw = Math.round(clamp(Math.min((h * 0.18 - 7) / 0.62, w * (portrait ? 0.42 : 0.2)), 12, 80))
        const isle = islandSprite(iw, rand)
        const x = Math.round((w - isle.canvas.width) / 2)
        const y = h - Math.max(2, Math.round(h * 0.03)) - isle.canvas.height
        this.floaters.push({ sprite: isle.canvas, x, y, amp: 1.2, freq: 1.1, phase: 0 })
        // two pebbles that broke off and float along beside it
        const a = rockSprite(Math.max(2, Math.round(iw * 0.1)), 2)
        const b = rockSprite(2, 2)
        this.floaters.push({ sprite: a, x: x + isle.left - a.width - 3, y: y + isle.ground + 4, amp: 1.4, freq: 1.4, phase: 1.3 })
        this.floaters.push({ sprite: b, x: x + isle.right + 4, y: y + isle.ground + Math.round(isle.soil * 0.6), amp: 1.1, freq: 1.7, phase: 2.6 })
    }

    /* `motion` is 0 for reduced motion: the frame is painted, nothing moves */
    draw(ctx: CanvasRenderingContext2D, dt: number, motion: number): void {
        this.t += dt * motion
        ctx.drawImage(this.sky, 0, 0)
        this.clouds.forEach((c) => {
            c.x += c.speed * dt * motion
            if (c.x > this.w) c.x -= this.w + c.sprite.width
            ctx.drawImage(c.sprite, Math.round(c.x), c.y)
        })
        this.floaters.forEach((f) => {
            const bob = Math.round(Math.sin(this.t * f.freq + f.phase) * f.amp)
            ctx.drawImage(f.sprite, f.x, f.y + bob)
        })
    }
}

/* ── Portrait ─────────────────────────────────────────────────────────── */

const PORTRAIT = 40

/* a head-and-shoulders bust: auburn bob, teal shirt, plum outline */
const portraitFigure = (): Grid => {
    const g = new Grid(PORTRAIT, PORTRAIT)
    const cx = 20
    const fringe = (x: number): number => 12 + Math.round((x - 12) * 0.2)
    const inHead = (x: number, y: number): boolean => ((x + 0.5 - cx) / 7.2) ** 2 + ((y + 0.5 - 18) / 8.8) ** 2 <= 1
    const inFace = (x: number, y: number): boolean =>
        ((x + 0.5 - cx) / 6.4) ** 2 + ((y + 0.5 - 18.6) / 8.2) ** 2 <= 1 && y >= fringe(x)
    const inHair = (x: number, y: number): boolean =>
        ((x + 0.5 - cx) / 9.6) ** 2 + ((y + 0.5 - 15.2) / 9.2) ** 2 <= 1 && y <= 23 && !inFace(x, y)

    // shoulders and shirt, shaded on the right, caught by light on the left
    for (let y = 29; y < PORTRAIT; y++) {
        const half = Math.min(16, 6 + (y - 29) * 2.2)
        for (let x = Math.round(cx - half); x < Math.round(cx + half); x++) {
            const across = (x + 0.5 - cx) / half
            const shade = across > 0.55 && bayer(x, y) < 0.5
            const lit = across < -0.6 && bayer(x, y) < 0.35
            g.set(x, y, shade ? PAL.plum : lit ? PAL.sage : PAL.teal)
        }
    }
    // neck, in shadow under the chin
    for (let y = 24; y <= 30; y++) {
        for (let x = 17; x <= 22; x++) g.set(x, y, y <= 26 ? PAL.peach : PAL.tan)
    }
    // open collar
    for (let y = 29; y <= 32; y++) {
        const open = 32 - y
        for (let x = cx - open - 1; x <= cx + open; x++) g.set(x, y, PAL.tan)
        g.set(cx - open - 2, y, PAL.creamHi)
        g.set(cx + open + 1, y, PAL.creamHi)
    }
    g.set(19, 33, PAL.creamHi)
    g.set(20, 33, PAL.creamHi)
    // head
    for (let y = 8; y <= 27; y++) {
        for (let x = 11; x <= 29; x++) {
            if (!inHead(x, y)) continue
            const shade = (x + 0.5 - cx) / 7.2 > 0.45 && bayer(x, y) < 0.5
            g.set(x, y, shade ? PAL.peach : PAL.tan)
        }
    }
    // hair: a bob with a side fringe and a shine across the crown
    for (let y = 4; y <= 23; y++) {
        for (let x = 9; x <= 31; x++) {
            if (!inHair(x, y)) continue
            const shine = y >= 8 && y <= 9 && x >= 14 && x <= 21 && bayer(x, y) < 0.6
            const shadow = y >= 21 || (x + 0.5 - cx > 6 && bayer(x, y) < 0.5)
            g.set(x, y, shine ? PAL.peach : shadow ? PAL.plum : PAL.rust)
        }
    }
    // brows, eyes, cheeks, nose, smile
    g.set(15, 17, PAL.rust)
    g.set(16, 17, PAL.rust)
    g.set(23, 17, PAL.rust)
    g.set(24, 17, PAL.rust)
    g.set(16, 19, PAL.plum)
    g.set(16, 20, PAL.plum)
    g.set(23, 19, PAL.plum)
    g.set(23, 20, PAL.plum)
    g.set(15, 22, PAL.peach)
    g.set(24, 22, PAL.peach)
    g.set(20, 21, PAL.peach)
    g.set(18, 23, PAL.rust)
    g.set(21, 23, PAL.rust)
    g.set(19, 24, PAL.rust)
    g.set(20, 24, PAL.rust)

    g.outline(PAL.plum)
    return g
}

/* the drawn portrait, shown until (or instead of) a photo */
export const paintPortraitSprite = (c: HTMLCanvasElement): void => {
    c.width = PORTRAIT
    c.height = PORTRAIT
    const ctx = c.getContext('2d')
    if (!ctx) return
    const bg = new Grid(PORTRAIT, PORTRAIT)
    for (let y = 0; y < PORTRAIT; y++) {
        for (let x = 0; x < PORTRAIT; x++) {
            const f = (x + y) / (PORTRAIT * 2 - 2)
            bg.set(x, y, f * 1.15 - 0.1 > bayer(x, y) ? PAL.sage : PAL.cream)
        }
    }
    bg.paint(ctx)
    portraitFigure().paint(ctx)
}

const PHOTO_PAL: RGB[] = [PAL.plum, PAL.rust, PAL.peach, PAL.tan, PAL.cream, PAL.creamHi, PAL.sage, PAL.teal].map(hexRgb)

/* a photo, cropped square, shrunk to the portrait grid and ordered-dithered
 * into the palette. A photo from a host that blocks reading its pixels is
 * shown shrunk but in its own colours. */
export const paintPortraitPhoto = (c: HTMLCanvasElement, img: HTMLImageElement): void => {
    const n = PORTRAIT
    const iw = img.naturalWidth || img.width
    const ih = img.naturalHeight || img.height
    if (!iw || !ih) return
    const tmp = makeCanvas(n, n)
    const tctx = tmp.getContext('2d')
    const ctx = c.getContext('2d')
    if (!tctx || !ctx) return
    const side = Math.min(iw, ih)
    tctx.fillStyle = PAL.cream
    tctx.fillRect(0, 0, n, n)
    tctx.imageSmoothingEnabled = true
    tctx.imageSmoothingQuality = 'high'
    // faces sit high in most portraits, so a tall photo is cropped from near the top
    tctx.drawImage(img, (iw - side) / 2, (ih - side) * 0.25, side, side, 0, 0, n, n)

    c.width = n
    c.height = n
    ctx.imageSmoothingEnabled = false
    let data: ImageData
    try {
        data = tctx.getImageData(0, 0, n, n)
    } catch {
        ctx.drawImage(tmp, 0, 0)
        return
    }
    const px = data.data
    for (let y = 0; y < n; y++) {
        for (let x = 0; x < n; x++) {
            const i = (y * n + x) * 4
            const nudge = (bayer(x, y) - 0.5) * 56
            const r = px[i] + nudge
            const g = px[i + 1] + nudge
            const b = px[i + 2] + nudge
            let best = PHOTO_PAL[0]
            let bestD = Infinity
            PHOTO_PAL.forEach((p) => {
                const d = (r - p[0]) ** 2 * 0.3 + (g - p[1]) ** 2 * 0.59 + (b - p[2]) ** 2 * 0.11
                if (d < bestD) {
                    bestD = d
                    best = p
                }
            })
            px[i] = best[0]
            px[i + 1] = best[1]
            px[i + 2] = best[2]
            px[i + 3] = 255
        }
    }
    ctx.putImageData(data, 0, 0)
}
